import { Modal } from "components";
import { useEffect, useCallback, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import SelectWifiNetworkModal from "./SelectWifiNetworkModal";
import { getNetworkSettings, updateNetworkSettings } from "actions/dashboard";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircle, WarningRounded } from "@mui/icons-material";
import {
  ConfigureNetwork,
  EnterWifiNetwork,
  LinearLoader,
} from "empower-energy-owner-components";

const styles = {
  "& .button-footer": {
    mt: 1,
    pt: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    borderTop: "1px solid rgba(0, 64, 87, 0.25)",
  },
  "& .updating-message": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    wordBreak: "break-word",
    "& .check-icon": {
      marginRight: "12px",
      fontSize: "40px",
    },
    "& .warning-icon": {
      color: "#F9B426",
      marginRight: "12px",
      fontSize: "40px",
    },
  },
};

const STATUS = Object.freeze({
  configure: "configure",
  selectWifi: "selectWifi",
  enterWifi: "enterWifi",
});

const MESSAGE_STATUS = Object.freeze({
  success: "success",
  fail: "fail",
  customMessage: "customMessage",
  systemMessage: "systemMessage",
});

const NetworkModal = ({ open, onClose, selectedUnit }) => {
  const dispatch = useDispatch();

  // UI State
  const [status, setStatus] = useState(STATUS.configure);
  const [selectedWifi, setSelectedWifi] = useState(null);
  const [settingsUpdated, setSettingsUpdated] = useState(false);
  const [messageStatus, setMessageStatus] = useState(
    MESSAGE_STATUS.customMessage
  );

  // Redux Selectors
  const networkSettings = useSelector(
    (state) => state.dashboard?.networkSettings
  );
  const previousNetworkSettings = useSelector(
    (state) => state.dashboard.previousNetworkSettings
  );
  const networkSettingsLoading = useSelector(
    (state) => state.dashboard.networkSettingsLoading
  );

  // Fetch network settings when modal opens or unit changes
  useEffect(() => {
    if (selectedUnit?.id) {
      dispatch(getNetworkSettings(selectedUnit.id));
    }
  }, [dispatch, selectedUnit?.id]);

  // Poll network settings when update in progress
  useEffect(() => {
    let interval;
    if (settingsUpdated) {
      // Immediately trigger
      dispatch(getNetworkSettings(selectedUnit.id));
      interval = setInterval(() => {
        dispatch(getNetworkSettings(selectedUnit.id));
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
    // Only trigger when settingsUpdated OR selectedUnit.id changes
  }, [settingsUpdated, dispatch, selectedUnit.id]);

  // Derive message status based on network update status
  useEffect(() => {
    const lastPrev = previousNetworkSettings?.status?.last_message;
    const lastCurr = networkSettings?.status?.last_message;
    if (!lastPrev || lastCurr > lastPrev) {
      if (!networkSettings?.status?.updating) {
        setMessageStatus(
          networkSettings?.status?.success
            ? MESSAGE_STATUS.success
            : MESSAGE_STATUS.fail
        );
      } else {
        setMessageStatus(MESSAGE_STATUS.systemMessage);
      }
    } else {
      setMessageStatus(MESSAGE_STATUS.customMessage);
    }
  }, [networkSettings, previousNetworkSettings]);

  const getTitle = useCallback(() => {
    switch (status) {
      case STATUS.configure:
        return "Configure Network";
      case STATUS.selectWifi:
        return "Select WiFi Network";
      case STATUS.enterWifi:
        if (selectedWifi?.ssid === "enterDetails") {
          return "Enter WiFi network";
        }
        return `Enter the password for “${selectedWifi?.ssid || selectedWifi?.bssid
          }”`;
      default:
        return "Configure Network";
    }
  }, [status, selectedWifi]);

  // Callbacks
  const handleSelectWifiNetwork = useCallback((wifi) => {
    setSelectedWifi(wifi);
    setStatus(STATUS.enterWifi);
  }, []);

  const handleConnectWifi = useCallback(
    (data) => {
      setSettingsUpdated(false);
      dispatch(
        updateNetworkSettings(selectedUnit.id, data, () => {
          setSettingsUpdated(true);
        })
      );
    },
    [dispatch, selectedUnit.id]
  );

  const handleConfigureNetwork = useCallback(
    (data) => {
      setSettingsUpdated(false);
      dispatch(
        updateNetworkSettings(selectedUnit.id, data, () => {
          setSettingsUpdated(true);
        })
      );
    },
    [dispatch, selectedUnit.id]
  );

  // Reset modal state if closed
  useEffect(() => {
    if (!open) {
      setStatus(STATUS.configure);
      setSelectedWifi(null);
      setSettingsUpdated(false);
      setMessageStatus(MESSAGE_STATUS.customMessage);
    }
  }, [open]);

  const showConfigure = !settingsUpdated && status === STATUS.configure;
  const showSelectWifi = !settingsUpdated && status === STATUS.selectWifi;
  const showEnterWifi = !settingsUpdated && status === STATUS.enterWifi;

  return (
    <Modal
      width="520px"
      top="10%"
      title={getTitle()}
      open={open}
      onClose={onClose}
      showHeaderBorder
      fullHeight={selectedWifi?.ssid === "enterDetails"}
    >
      <Box mt={2}>
        {!settingsUpdated && (
          <Box>
            {showConfigure && (
              <ConfigureNetwork
                loading={networkSettingsLoading}
                networkSettings={networkSettings}
                onCancel={onClose}
                onClickSave={handleConfigureNetwork}
                onClickSelectNetwork={() => setStatus(STATUS.selectWifi)}
              />
            )}

            {showSelectWifi && (
              <SelectWifiNetworkModal
                selectedUnit={selectedUnit}
                onCancel={() => setStatus(STATUS.configure)}
                onClickSelectWifiNetwork={handleSelectWifiNetwork}
              />
            )}

            {showEnterWifi && (
              <EnterWifiNetwork
                selectedWifi={selectedWifi}
                onCancel={() => setStatus(STATUS.selectWifi)}
                onClickSave={handleConnectWifi}
              />
            )}
          </Box>
        )}

        {settingsUpdated && (
          <Box sx={styles}>
            <Box py={1}>
              {messageStatus === MESSAGE_STATUS.success && (
                <Box className="updating-message">
                  <CheckCircle className="check-icon" color="success" />
                  <Typography variant="h4">
                    {networkSettings?.status?.message}
                  </Typography>
                </Box>
              )}

              {messageStatus === MESSAGE_STATUS.fail && (
                <Box className="updating-message">
                  <WarningRounded className="warning-icon" />
                  <Typography variant="h4">
                    {networkSettings?.status?.message}
                  </Typography>
                </Box>
              )}

              {messageStatus === MESSAGE_STATUS.customMessage && (
                <Box width="100%">
                  <LinearLoader message="Updating network settings (custom)" />
                </Box>
              )}

              {messageStatus === MESSAGE_STATUS.systemMessage && (
                <Box width="100%">
                  <LinearLoader
                    message={
                      networkSettings?.status?.message ||
                      "Updating network settings"
                    }
                  />
                </Box>
              )}
            </Box>
            <Box className="button-footer">
              <Button onClick={onClose}>Dismiss</Button>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default NetworkModal;
